import {
  SurfaceBody,
  SurfaceCloseButton,
  SurfaceFooter,
  SurfaceHandle,
  SurfaceHeader,
  type SurfaceHeaderProps,
  SurfaceTitle,
} from "@ui/components/SheetChrome";
import { SheetSurface, type SheetSurfaceRootProps, useSheetSurface } from "@ui/components/SheetSurface";
import type { ReactNode } from "react";
import { dialogBackdrop, dialogPanel } from "./dialog.styles";

export type DialogRootProps = SheetSurfaceRootProps;

/**
 * Adaptive task dialog: a detached centered card on desktop (≥md), a
 * native-style bottom card sheet on mobile (iOS card / M3 skins), with
 * swipe-to-dismiss, scroll handoff, focus containment, scroll lock, Escape and
 * scrim dismissal supplied by the surface.
 *
 * Open state: controlled (`isOpen`/`onOpenChange`), uncontrolled
 * (`defaultOpen`), or trigger-driven.
 *
 * ```tsx
 * <Dialog.Root isOpen={open} onOpenChange={setOpen}>
 *   <Dialog.Content>
 *     <Dialog.Header title={t("…")} />
 *     <Dialog.Body>…</Dialog.Body>
 *     <Dialog.Footer>…</Dialog.Footer>
 *   </Dialog.Content>
 * </Dialog.Root>
 * ```
 *
 * When to use this vs `Page` vs `ActionMenu` vs `PersistentSheet`: see
 * Foundations/Overlays in Storybook.
 */
function DialogRoot(props: DialogRootProps) {
  return <SheetSurface.Root {...props} />;
}

export interface DialogContentProps {
  children: ReactNode;
  /** Extra classes for the traveling panel. */
  className?: string;
}

/** The Dialog's configuration of the surface: centred card, or bottom sheet. */
function DialogContent({ children, className }: DialogContentProps) {
  const { platform, chrome, size, dismissal, grabber } = useSheetSurface();
  const desktop = platform === "desktop";

  return (
    <SheetSurface.View
      backdropClassName={dialogBackdrop({ platform })}
      className={dialogPanel({ chrome, class: className, platform, size })}
      placement={desktop ? "center" : "bottom"}
      swipe={!desktop}
    >
      {grabber && dismissal.dismissible && !desktop && <SurfaceHandle />}
      {children}
    </SheetSurface.View>
  );
}

/** The Dialog's header treatment: the compact sheet title bar on mobile. */
function DialogHeader(props: Omit<SurfaceHeaderProps, "layout">) {
  return <SurfaceHeader {...props} layout="sheet" />;
}

export const Dialog = {
  Body: SurfaceBody,
  CloseButton: SurfaceCloseButton,
  Content: DialogContent,
  Footer: SurfaceFooter,
  Header: DialogHeader,
  Root: DialogRoot,
  Title: SurfaceTitle,
};
